import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { cn } from '@/lib/utils';
import { ModernPlayIcon, PlayIconTheme } from '@/components/ui/icons/ModernPlayIcon';
import { ThemedIcon } from './ThemedIcon';

interface BigPlayButtonProps {
  onClick: () => void;
  iconType: PlayIconTheme;
  /** Cor do ícone (Cor do play grande / controlColorIcon). */
  color?: string;
  /** Cor do círculo de fundo (Cor dos controles / controlColor). */
  circleColor?: string;
  /** Voltar 10s — só no tema Safari, quando a feature rewind está ativa. */
  onSkipBack?: () => void;
  /** Avançar 10s — só no tema Safari, quando a feature skipForward está ativa. */
  onSkipForward?: () => void;
}

// Efeito "vidro" (glassmorphism): mesmo dos botões do rodapé no tema Safari —
// círculo translúcido com blur + anel sutil.
const GLASS =
  'bg-white/10 backdrop-blur-md ring-1 ring-white/25 shadow-sm transition-colors hover:bg-white/20';

export const BigPlayButton: React.FC<BigPlayButtonProps> = ({
  onClick,
  iconType,
  color = '#FFFFFF',
  circleColor,
  onSkipBack,
  onSkipForward,
}) => {
  useLanguage();
  // Tema Safari: o play grande vira o cluster central da imagem do Safari
  // (voltar 10s + play + avançar 10s), todos com efeito de vidro e espaçados.
  // Os botões de skip só aparecem quando os respectivos handlers chegam — o
  // VideoFeatureOverlays os gateia nas features rewind/skipForward.
  if (iconType === 'safari') {
    return (
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-6">
        {onSkipBack && (
          <button
            onClick={onSkipBack}
            className={cn(
              'w-[52px] h-[52px] rounded-full flex items-center justify-center cursor-pointer',
              GLASS
            )}
            style={{ color }}
            aria-label={translateInterfaceText("Voltar 10 segundos")}
          >
            <div className="w-[28px] h-[28px] flex items-center justify-center">
              <ThemedIcon name="skipBack" theme="safari" className="h-full w-full" />
            </div>
          </button>
        )}

        <button
          onClick={onClick}
          className={cn(
            'w-[66px] h-[66px] rounded-full flex items-center justify-center cursor-pointer',
            GLASS
          )}
          style={{ color }}
          aria-label={translateInterfaceText("Reproduzir")}
        >
          <div className="w-[30px] h-[30px] flex items-center justify-center">
            <ModernPlayIcon iconType={iconType} className="h-full w-full" />
          </div>
        </button>

        {onSkipForward && (
          <button
            onClick={onSkipForward}
            className={cn(
              'w-[52px] h-[52px] rounded-full flex items-center justify-center cursor-pointer',
              GLASS
            )}
            style={{ color }}
            aria-label={translateInterfaceText("Avançar 10 segundos")}
          >
            <div className="w-[28px] h-[28px] flex items-center justify-center">
              <ThemedIcon name="skipForward" theme="safari" className="h-full w-full" />
            </div>
          </button>
        )}
      </div>
    );
  }

  // Demais temas: círculo único com cor sólida do "Cor dos controles" e ícone
  // com a "Cor do play grande". Sem animações — visual limpo e direto.
  const bg = circleColor || 'rgba(0, 0, 0, 0.7)';

  return (
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      <button
        onClick={onClick}
        className="w-[65px] h-[65px] rounded-full flex items-center justify-center cursor-pointer"
        style={{ color, backgroundColor: bg }}
      >
        {/* Ícone ~50x50 dentro do círculo de 65x65 (proporção do runner.js).
            O glyph muda por tema (minimal/solid/duotone/bold/safari). */}
        <div className="w-[50px] h-[50px] flex items-center justify-center">
          <ModernPlayIcon iconType={iconType} className="h-full w-full" />
        </div>
      </button>
    </div>
  );
};
